import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, signal } from '@angular/core';
import { Observable, Subject, Subscription, of } from 'rxjs';
import { catchError, debounceTime, map, switchMap } from 'rxjs/operators';
import { BrandFetchService, BrandSuggestionResponse } from '../../../onboarding/service/brand-fetch/brand-fetch.service';
import { normalizeDomain } from '../../../onboarding-value-first/services/value-first-onboarding.types';
import { ValueFirstAgencyService } from '../../services/value-first-agency.service';

type BrandSearchResult =
  | { status: 'cleared' }
  | { status: 'failed' }
  | { status: 'done'; results: BrandSuggestionResponse[] };

@Component({
  selector: 'vfa-website-step',
  templateUrl: './website-step.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VfaWebsiteStep implements OnDestroy {
  readonly onboarding = inject(ValueFirstAgencyService);
  private readonly brandFetch = inject(BrandFetchService);
  readonly domain = computed(() => normalizeDomain(this.onboarding.draft().url));
  readonly suggestions = signal<BrandSuggestionResponse[] | null>(null);

  private readonly querySubject = new Subject<string>();
  private readonly subscriptionList = new Subscription();

  constructor() {
    this.subscriptionList.add(
      this.querySubject.pipe(
        debounceTime(250),
        switchMap((query): Observable<BrandSearchResult> => {
          const trimmed = query.trim();
          if (trimmed.length <= 3) {
            return of({ status: 'cleared' });
          }
          return this.brandFetch.brandSearch(trimmed).pipe(
            map((results) => ({ status: 'done' as const, results })),
            catchError(() => of({ status: 'failed' as const })),
          );
        }),
      ).subscribe((outcome) => {
        if (this.onboarding.draft().url.trim().length <= 3 || outcome.status !== 'done') {
          this.suggestions.set(null);
          return;
        }
        this.suggestions.set(outcome.results);
      }),
    );
  }

  onUrl(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.onboarding.setUrl(value);
    if (value.trim().length <= 3) {
      this.suggestions.set(null);
    }
    this.querySubject.next(value);
  }

  select(suggestion: BrandSuggestionResponse): void {
    void this.onboarding.selectSuggestion(suggestion);
  }

  submit(event: Event): void {
    event.preventDefault();
    void this.onboarding.findAgency();
  }

  manual(): void {
    this.onboarding.openManual();
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
    this.querySubject.complete();
  }
}
